import { Component, computed, inject, signal } from '@angular/core';
import { Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ColumnConfig, CustomAction, FieldType, FilterConfig, GenericTableComponent, GlobalAction, HeaderConfigurationService, ModalTabConfig, TableAction, TableActionConfig } from '@solcre-org/core-ui';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment.local';
import { PortModel } from '../../../shared/models/port.model';
import { Country } from '../../../shared/models/country.model';
import { COUNTRIES } from '../../../shared/data/countries';
import { TIMEZONES } from '../../../shared/data/timezones';
import { PortBerthingTypes } from '../../../shared/enums/port-berthing-types.enum';
import { UNLOCODE_PATTERN_VALIDATOR } from '../../../shared/validators/pattern-validators';

@Component({
  selector: 'ports',
  imports: [GenericTableComponent, TranslateModule],
  templateUrl: './ports.html',
})
export class Ports {

  private headerConfigurationService = inject(HeaderConfigurationService);
  private translateService: TranslateService = inject(TranslateService);
  private router: Router = inject(Router);

  endpoint = environment.API.PORTS_URI;
  ports = signal<PortModel[]>([]);
  countries: Country[] = COUNTRIES;
  timezones = TIMEZONES;

  columns: ColumnConfig<PortModel>[] = [
    {
      label: 'ports.portCode',
      key: 'portCode',
    },
    {
      label: 'ports.fullName',
      key: 'fullName',
    },
    {
      label: 'ports.terminal',
      key: 'terminal',
    },
    {
      label: 'ports.country',
      key: 'country',
    },
    {
      label: 'ports.timezone',
      key: 'timezone',
    },
    {
      label: 'ports.berthingType',
      key: 'berthingType',
      format: (value: string) => {
        return this.translateService.instant(`portBerthingTypes.${value}`);
      }
    },
    {
      label: 'ports.overtimeReady',
      key: 'overtimeReady',
      format: (_value: string, row: PortModel) => {
        return this.translateService.instant(
          row.country && row.timezone ? 'ports.overtimeReadyYes' : 'ports.overtimeReadyNo'
        );
      }
    }
  ];

  modalFields = computed<ModalTabConfig<PortModel>[]>(() => [
    {
      tabId: 'general_data',
      tabLabel: 'ports.general_data',
      fields: [
        {
          label: 'ports.portCode',
          placeholder: 'ports.portCodePlaceholder',
          key: 'portCode',
          type: FieldType.TEXT,
          readonly: false,
          validators: [Validators.required, UNLOCODE_PATTERN_VALIDATOR],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
            pattern: this.translateService.instant('validators.unlocodePattern'),
          }
        },
        {
          label: 'ports.fullName',
          placeholder: 'ports.fullNamePlaceholder',
          key: 'fullName',
          type: FieldType.TEXT,
          readonly: false,
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          }
        },
        {
          label: 'ports.terminal',
          placeholder: 'ports.terminalPlaceholder',
          key: 'terminal',
          type: FieldType.TEXT,
          readonly: false,
        },
        {
          label: 'ports.country',
          placeholder: 'ports.countryPlaceholder',
          key: 'country',
          type: FieldType.SELECT,
          searchable: true,
          defaultValue: '',
          options: this.countries,
          readonly: false,
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          }
        },
        {
          label: 'ports.timezone',
          placeholder: 'ports.timezonePlaceholder',
          key: 'timezone',
          type: FieldType.SELECT,
          searchable: true,
          defaultValue: '',
          options: this.timezones,
          readonly: false,
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          }
        },
        {
          key: 'berthingType',
          placeholder: 'ports.berthingTypePlaceholder',
          label: this.translateService.instant('ports.berthingType'),
          type: FieldType.SELECT,
          searchable: true,
          readonly: false,
          defaultValue: null,
          options: Object.values(PortBerthingTypes).map((value) => ({
            label: this.translateService.instant(`portBerthingTypes.${value}`),
            value: value,
          })),
          validators: [Validators.required],
          errorMessages: {
            required: this.translateService.instant('validators.required'),
          }
        },
      ],
    },
  ] as any as ModalTabConfig<PortModel>[]);

  customFilters = computed<FilterConfig<PortModel>[]>(() => [
    {
      key: 'portCode',
      label: 'ports.portCode',
      placeholder: 'ports.portCodePlaceholder',
      type: FieldType.TEXT,
      readonly: false,
      filterFn: (row: PortModel, value: any) => value,
    },
    {
      key: 'fullName',
      label: 'ports.fullName',
      placeholder: 'ports.fullNamePlaceholder',
      type: FieldType.TEXT,
      readonly: false,
      filterFn: (row: PortModel, value: any) => value,
    },
    {
      key: 'country',
      label: 'ports.country',
      placeholder: 'ports.countryPlaceholder',
      type: FieldType.SELECT,
      searchable: true,
      options: this.countries,
      readonly: false,
      filterFn: (row: PortModel, value: any) => value,
    },
  ]);

  globalActions: GlobalAction<PortModel>[] = [];
  customActions: CustomAction<PortModel>[] = [
    {
      title: 'Tariffs',
      icon: 'fa-regular fa-file-invoice-dollar',
      isExtra: false,
      callback: (row: PortModel) => {
        this.router.navigate(['/app/ports', row.id, 'tariffs'], {
          queryParams: {
            portCode: row.portCode,
            portName: row.fullName,
            terminal: row.terminal,
            country: row.country,
            berthingType: row.berthingType,
          },
        });
      },
    },
    {
      title: 'Overtime Rules',
      icon: 'fa-regular fa-clock',
      isExtra: false,
      callback: (row: PortModel) => {
        this.router.navigate(['/app/ports', row.id, 'overtime-rules'], {
          queryParams: {
            portCode: row.portCode,
            portName: row.fullName,
            terminal: row.terminal,
            country: row.country,
            timezone: row.timezone,
            berthingType: row.berthingType,
          },
        });
      },
    },
  ];
  actions: TableActionConfig[] = [
    { action: TableAction.VIEW, isExtra: false },
    { action: TableAction.EDIT, isExtra: false },
    { action: TableAction.DELETE, isExtra: true },
    { action: TableAction.SELECT, isExtra: false },
    { action: TableAction.CREATE, isExtra: false },
  ];

  ngOnInit() {
    this.headerConfigurationService.setEndpointHeaders(this.endpoint, {
      patch: [
        { 'Content-Type': 'application/merge-patch+json' },
      ]
    });
  }

  portsModelFactory = (data: Partial<PortModel>): PortModel => {
    return new PortModel().fromJSON(data);
  };

  handleApiError = (error: any, defaultMessage: string, operation: string): string => {
    const errObj = error?.error || error;
    if (errObj?.status === 422 && Array.isArray(errObj?.violations)) {
      const duplicateViolation = errObj.violations.find((v: any) =>
        v.code === '23bd9dbf-6b9b-41cd-a99e-4844bcf3077f' ||
        v.message?.includes('this combination')
      );
      if (duplicateViolation) {
        return this.translateService.instant('ports.errors.duplicatePort');
      }

      const fieldViolation = errObj.violations.find((v: any) =>
        v.propertyPath === 'country' || v.propertyPath === 'timezone'
      );
      if (fieldViolation) {
        return this.translateService.instant('ports.errors.invalidField', {
          field: this.translateService.instant(`ports.${fieldViolation.propertyPath}`),
          message: fieldViolation.message,
        });
      }
    }
    return defaultMessage;
  };
}
